import React from 'react';
import clsx from 'clsx';

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number;
}

interface TabBarProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
  /** 'pill' = segmented pill (default). 'underline' = sticky border-bottom tabs. */
  variant?: 'pill' | 'underline';
  className?: string;
}

/**
 * Unified tab bar used across all feature views.
 * Pill variant: segmented control with white active pill.
 * Underline variant: horizontal scroll with brand bottom border.
 */
export const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTab,
  onChange,
  variant = 'pill',
  className,
}) => {
  if (variant === 'underline') {
    return (
      <div
        role="tablist"
        className={clsx(
          'flex overflow-x-auto border-b border-slate-200 dark:border-slate-700 no-scrollbar',
          className
        )}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => onChange(tab.id)}
            className={clsx(
              'relative flex items-center gap-2 px-4 py-3.5 border-b-2 text-sm font-medium whitespace-nowrap transition-colors capitalize',
              activeTab === tab.id
                ? 'border-brand text-brand'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            )}
          >
            {tab.icon}
            {tab.label}
            {!!tab.badge && tab.badge > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={clsx(
        'flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl overflow-x-auto no-scrollbar',
        className
      )}
    >
      {tabs.map(tab => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={activeTab === tab.id}
          onClick={() => onChange(tab.id)}
          className={clsx(
            'relative flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all',
            activeTab === tab.id
              ? 'bg-white dark:bg-slate-900 text-brand shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          )}
        >
          {tab.icon}
          {tab.label}
          {!!tab.badge && tab.badge > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white">
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};
